/**
 * Componente Header Inmersivo
 * Cabecera transparente que cambia según el scroll
 * Estilo tipo Heinz/Apple con transiciones suaves
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Logo from '../common/Logo';
import LocalizedLink from '../common/LocalizedLink';
import LanguageSelector from '../common/LanguageSelector';
import { stripLanguagePrefix } from '../../i18n/paths';

gsap.registerPlugin(ScrollTrigger);

/**
 * Header inmersivo con cambio dinámico de estilos
 * - Transparente al inicio
 * - Fondo sólido al hacer scroll
 * - Menú hamburguesa animado
 * - Enlaces y textos según el idioma de la URL, con selector de idioma
 * 
 * @returns {JSX.Element} Componente Header
 */
const ImmersiveHeader = () => {
  const headerRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollY = useRef(0);
  const location = useLocation();
  const { t } = useTranslation();

  // Ruta actual sin prefijo de idioma, para marcar el enlace activo
  const currentPath = stripLanguagePrefix(location.pathname);

  // Enlaces de navegación (rutas en español; el texto viene de las traducciones)
  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.products'), path: '/productos' },
    { name: t('nav.recipes'), path: '/recetas' },
    { name: t('nav.about'), path: '/nosotros' },
    { name: t('nav.news'), path: '/noticias' },
    { name: t('nav.transparency'), path: '/transparencia' },
    { name: t('nav.contact'), path: '/contacto' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Cambiar estado de scroll
      setIsScrolled(currentScrollY > 50);
      
      // Ocultar/mostrar header al hacer scroll
      if (currentScrollY > lastScrollY.current && currentScrollY > 200) {
        setIsHidden(true);
      } else {
        setIsHidden(false);
      }
      
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cerrar menú al cambiar de ruta
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Animación del menú
  useEffect(() => {
    const menuItems = document.querySelectorAll('.nav-link-item');
    
    if (isMenuOpen) {
      gsap.fromTo(menuItems,
        { y: 50, opacity: 0 },
        { 
          y: 0, 
          opacity: 1, 
          duration: 0.5, 
          stagger: 0.1,
          ease: 'power3.out',
        }
      );
    }
  }, [isMenuOpen]);

  return (
    <>
      {/* Header principal */}
      <header
        ref={headerRef}
        className={`
          fixed top-0 left-0 right-0 z-50
          transition-all duration-500 ease-out
          ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}
          ${isHidden && !isMenuOpen ? '-translate-y-full' : 'translate-y-0'}
        `}
      >
        <div className="container mx-auto px-4 md:px-8">
          {/* Móvil/tablet: logo y hamburguesa en los extremos. Desktop (xl): rejilla
              de tres columnas con los laterales iguales (1fr), para que la
              navegación quede centrada respecto a la cabecera y no respecto
              al hueco que dejan el logo y el selector de idioma. */}
          <div className="flex items-center justify-between h-20 md:h-24 xl:grid xl:grid-cols-[1fr_auto_1fr] xl:gap-8">
            {/* Logo */}
            <LocalizedLink 
              to="/" 
              className="relative z-50 transition-transform duration-300 hover:scale-105 xl:justify-self-start"
            >
              <Logo 
                className={`h-12 md:h-16 transition-all duration-300 ${
                  isScrolled || isMenuOpen ? '' : 'brightness-0 invert'
                }`}
              />
            </LocalizedLink>

            {/* Navegación Desktop, centrada */}
            <nav className="hidden xl:flex items-center gap-5 2xl:gap-8">
              {navLinks.map((link) => (
                <LocalizedLink
                  key={link.path}
                  to={link.path}
                  className={`
                    relative font-lato font-medium text-sm uppercase tracking-wider whitespace-nowrap
                    transition-colors duration-300
                    ${isScrolled 
                      ? 'text-intercasa-dark hover:text-intercasa-red' 
                      : 'text-white hover:text-intercasa-yellow'
                    }
                    ${currentPath === link.path ? 'font-bold' : ''}
                  `}
                >
                  {link.name}
                  {/* Línea activa */}
                  <span 
                    className={`
                      absolute -bottom-1 left-0 h-0.5 bg-intercasa-red
                      transition-all duration-300
                      ${currentPath === link.path ? 'w-full' : 'w-0'}
                    `}
                  />
                </LocalizedLink>
              ))}
            </nav>

            {/* Selector de idioma (desktop), alineado a la derecha */}
            <LanguageSelector
              tone={isScrolled ? 'dark' : 'light'}
              className="hidden xl:flex xl:justify-self-end"
            />

            {/* Botón hamburguesa */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="relative z-50 w-10 h-10 flex flex-col items-center justify-center gap-1.5 xl:hidden"
              aria-label={isMenuOpen ? t('header.closeMenu') : t('header.openMenu')}
            >
              <span 
                className={`
                  block w-6 h-0.5 transition-all duration-300
                  ${isScrolled || isMenuOpen ? 'bg-intercasa-dark' : 'bg-white'}
                  ${isMenuOpen ? 'rotate-45 translate-y-2' : ''}
                `}
              />
              <span 
                className={`
                  block w-6 h-0.5 transition-all duration-300
                  ${isScrolled || isMenuOpen ? 'bg-intercasa-dark' : 'bg-white'}
                  ${isMenuOpen ? 'opacity-0' : ''}
                `}
              />
              <span 
                className={`
                  block w-6 h-0.5 transition-all duration-300
                  ${isScrolled || isMenuOpen ? 'bg-intercasa-dark' : 'bg-white'}
                  ${isMenuOpen ? '-rotate-45 -translate-y-2' : ''}
                `}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Menú móvil fullscreen */}
      <div
        className={`
          fixed inset-0 z-40 bg-white overflow-y-auto overscroll-contain
          transition-all duration-500 ease-out xl:hidden
          ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}
        `}
      >
        {/* min-h-full + padding superior del alto de la cabecera: el contenido
            queda centrado sin quedar debajo del logo y, si no cabe (móvil en
            horizontal), el menú hace scroll en lugar de cortarse. */}
        <div className="flex flex-col items-center justify-center min-h-full px-6 pt-24 pb-10 md:pt-28">
          <nav className="flex flex-col items-center gap-5 sm:gap-6 md:gap-8">
            {navLinks.map((link, index) => (
              <LocalizedLink
                key={link.path}
                to={link.path}
                className={`
                  nav-link-item
                  font-baskerville text-2xl sm:text-3xl md:text-4xl font-semibold text-center
                  text-intercasa-dark hover:text-intercasa-red
                  transition-colors duration-300
                  ${currentPath === link.path ? 'text-intercasa-red' : ''}
                `}
              >
                {link.name}
              </LocalizedLink>
            ))}
          </nav>

          {/* Selector de idioma (menú móvil), separado de la navegación */}
          <div className="mt-10 pt-8 border-t border-intercasa-dark/10">
            <LanguageSelector tone="dark" size="lg" />
          </div>

          {/* Redes sociales en el menú */}
          <div className="mt-12 flex gap-6">
            <a 
              href="https://www.instagram.com/intercasacanarias/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-intercasa-dark hover:text-intercasa-red transition-colors"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a 
              href="https://www.linkedin.com/company/intercasa-s-a-" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-intercasa-dark hover:text-intercasa-red transition-colors"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default ImmersiveHeader;
